'use client';

import { userSocials, type SocialPlatform } from '@/lib/socials';
import { cn } from '@/lib/utils';

/**
 * Brand marks. lucide ships no brand icons (its `X` is the close glyph, not the
 * logo), so these are inline paths sized to a 24-box and drawn in currentColor.
 */
const ICONS: Record<SocialPlatform, React.ReactNode> = {
  twitch: (
    <path d="M4 2 2.5 5.5v13H7V22h2.5l3-3.5H17l4.5-4.5V2H4Zm15.5 11L17 15.5h-4.5l-3 3v-3H6V4h13.5v9ZM15 7.5v5h-2v-5h2Zm-5 0v5H8v-5h2Z" />
  ),
  youtube: (
    <path d="M23 12s0-3.9-.5-5.8a3 3 0 0 0-2.1-2.1C18.5 3.5 12 3.5 12 3.5s-6.5 0-8.4.6A3 3 0 0 0 1.5 6.2C1 8.1 1 12 1 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 8.4.6 8.4.6s6.5 0 8.4-.6a3 3 0 0 0 2.1-2.1C23 15.9 23 12 23 12ZM9.8 15.6V8.4l6.2 3.6-6.2 3.6Z" />
  ),
  x: (
    <path d="M18.2 2H21l-6.4 7.3L22.3 22h-6l-4.7-6.2L6.2 22H3.4l6.9-7.9L2 2h6.1l4.3 5.7L18.2 2Zm-1 18.3h1.6L7.9 3.6H6.2l11 16.7Z" />
  ),
  tiktok: (
    <path d="M16.6 2h-3.2v13.7a2.5 2.5 0 1 1-2.5-2.5c.3 0 .5 0 .7.1v-3.2a6 6 0 0 0-.7 0 5.6 5.6 0 1 0 5.6 5.6V8.9a7 7 0 0 0 4.1 1.3V7a4.1 4.1 0 0 1-4-4Z" />
  ),
  instagram: (
    <path d="M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2Zm0 3.2A6.6 6.6 0 1 0 18.6 12 6.6 6.6 0 0 0 12 5.4Zm0 10.9A4.3 4.3 0 1 1 16.3 12 4.3 4.3 0 0 1 12 16.3Zm6.9-11.1a1.5 1.5 0 1 1-1.5-1.5 1.5 1.5 0 0 1 1.5 1.5Z" />
  ),
};

/**
 * The social accounts linked on a profile. Renders nothing when the user has
 * none, so callers can drop it in unconditionally.
 */
export default function SocialLinks({
  user,
  className,
}: {
  user: {
    twitch_handle?: string | null;
    youtube_handle?: string | null;
    x_handle?: string | null;
    tiktok_handle?: string | null;
    instagram_handle?: string | null;
  } | null | undefined;
  className?: string;
}) {
  const socials = userSocials(user);
  if (socials.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap items-center justify-center gap-2', className)}>
      {socials.map(({ platform, handle, url, label }) => (
        <a
          key={platform}
          href={url}
          target="_blank"
          rel="noopener noreferrer me"
          title={`${label}: ${platform === 'twitch' ? handle : `@${handle}`}`}
          aria-label={`${label} (opens in a new tab)`}
          className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-white/70 transition-colors hover:bg-white/10 hover:text-white md:gap-2 md:rounded-xl md:px-3 md:py-2 md:text-sm"
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            className="size-3.5 shrink-0 md:size-4"
          >
            {ICONS[platform]}
          </svg>
          <span className="font-medium">{handle}</span>
        </a>
      ))}
    </div>
  );
}
